/* istanbul ignore file */
import React, { useCallback, useState } from 'react';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';

import * as styles from './Header.styles';
import { searchBarContainerHidden } from './Header.styles';
import { ContentOverlay } from 'client-site-chrome-ui/components/atoms/ContentOverlay/ContentOverlay';
import { CuratorButton } from 'client-site-chrome-ui/components/atoms/Curator/Curator';
import { SiteLogo } from 'client-site-chrome-ui/components/atoms/SiteLogo/SiteLogo';
import { IconBar } from 'client-site-chrome-ui/components/molecules/IconBar/IconBar';
import { LocaleModal } from 'client-site-chrome-ui/components/molecules/LocaleModal/components/LocaleModal';
import { SearchBarContainer } from 'client-site-chrome-ui/components/molecules/SearchBarContainer/SearchBarContainer';
import { SearchSuggestionsContainer } from 'client-site-chrome-ui/components/molecules/SearchSuggestions/SearchSuggestionsContainer';
import { TopBanner } from 'client-site-chrome-ui/components/molecules/TopBanner/TopBanner';
import type { TopBannerMessage } from 'client-site-chrome-ui/content/types/TopBannerMessage';
import { SearchContextProvider } from 'client-site-chrome-ui/context/SearchContext/SearchContextProvider';
import { useSiteChromeDynamicContext } from 'client-site-chrome-ui/context/SiteChromeDynamicContext/useSiteChromeDynamicContext';
import { useSiteChromeStaticContext } from 'client-site-chrome-ui/context/SiteChromeStaticContext/useSiteChromeStaticContext';
import { UserContextProvider } from 'client-site-chrome-ui/context/UserContext/UserContextProvider';
import type { Locale } from 'client-site-chrome-ui/types/locale';

export interface HeaderProps {
  enableCurator?: boolean;
  stickyTopBanner?: boolean;
  topBannerMessages?: TopBannerMessage[];
}
export const Header = ({
  enableCurator = false,
  stickyTopBanner = false,
  topBannerMessages,
}: HeaderProps) => {
  const {
    locale: [currentLocale, setLocale],
    localeModal: { close, closeAfterSave, isOpen, placement },
  } = useSiteChromeDynamicContext();
  const { compact = false, env } = useSiteChromeStaticContext();
  const [searchBarVisible, setSearchBarVisible] = useState(!compact);

  const onSave = useCallback(
    (updatedLocale: Locale) => {
      setLocale(updatedLocale, placement);
      closeAfterSave();
    },
    [closeAfterSave, placement, setLocale],
  );

  const onClose = useCallback(() => close(), [close]);

  const toggleSearchBar = () => {
    sendGAEvent({
      event: 'custom_event',
      event_category: 'Header',
      event_action: searchBarVisible ? 'collapse' : 'expand',
      event_label: 'Search',
    });
    setSearchBarVisible((searchBarVisible) => !searchBarVisible);
  };

  return (
    <>
      {topBannerMessages && topBannerMessages.length > 0 && (
        <TopBanner messages={topBannerMessages} sticky={stickyTopBanner} />
      )}
      <header css={styles.header} data-nosnippet>
        <div css={styles.headerInner(searchBarVisible)}>
          <div css={styles.siteLogo}>
            <a href="/">
              <SiteLogo env={env} />
            </a>
          </div>
          <div css={styles.secondRowWrapper} data-has-curator={String(enableCurator)}>
            <SearchContextProvider>
              <div css={[styles.searchBarContainer, !searchBarVisible && searchBarContainerHidden]}>
                <SearchBarContainer enableCurator={enableCurator ?? false} />
                <SearchSuggestionsContainer />
              </div>
            </SearchContextProvider>
            {enableCurator && (
              <div css={styles.curatorIconContainer}>
                <CuratorButton aria-label="Ask The Curator" env={env} />
              </div>
            )}
          </div>
          <div css={styles.iconBar}>
            <UserContextProvider>
              <IconBar
                compact={compact}
                onSearchIconClick={toggleSearchBar}
                searchIconActive={searchBarVisible}
              />
            </UserContextProvider>
          </div>
        </div>
        <LocaleModal isOpen={isOpen} locale={currentLocale} onClose={onClose} onSave={onSave} />
      </header>
      <ContentOverlay />
    </>
  );
};
